export type ProductId = 'sago' | 'starch' | 'polishBroken' | 'vibratorBroken' | 'dryerBroken' | 'polishPowder';

export interface ProductionDaily {
    id?: string;
    unitId: string;            // e.g., STL, SKS, DV
    unitName?: string;         // Phase 2: For reports integration
    unit?: string;             // Alias for unitName
    productId: ProductId;
    productName?: string;      // Phase 2: Human-readable product name
    product?: string;          // Alias for productName
    date: string;              // YYYY-MM-DD (local)
    dateISO?: string;          // Alias for date
    openingStock: number;      // bags
    producedBags: number;      // bags
    quantity?: number;         // Alias for producedBags (Phase 2)
    quantityBags?: number;     // Alias for producedBags
    salesBags: number;         // bags
    closingStockExpected: number; // derived, stored for speed
    notes?: string;
    createdAt?: any;
    createdBy?: string;
    createdByName?: string;    // Phase 2: For audit trail
    updatedAt?: any;
    updatedBy?: string;
    updatedByName?: string;    // Phase 2: For audit trail
    
    // Phase 2: Audit fields
    isLocked?: boolean;
    lockedBy?: string;
    lockedAt?: any;
    lockReason?: string;
    
    // Phase 2: Status tracking
    status?: 'draft' | 'submitted' | 'approved' | 'rejected';
    approvedBy?: string;
    approvedAt?: any;
    rejectedBy?: string;
    rejectedAt?: any;
    rejectionReason?: string;
}

export interface ProductionAudit {
    id?: string;
    unitId: string;
    productId: ProductId;
    periodStartDate: string;   // YYYY-MM-DD
    periodEndDate: string;     // YYYY-MM-DD
    expectedClosingStock: number; // Σ(Open+Prod−Sales)
    auditedClosingStock: number;
    actualProducedBags: number;    // (Audited − Opening@Start) + ΣSales
    excessOrShortageBags: number;  // Audited − Expected
    totalExpense: number;          // Unit-level expense allocation
    costPerBag: number;            // totalExpense / actualProducedBags
    notes?: string;
    createdAt?: any;
    createdBy?: string;
    updatedAt?: any;
    updatedBy?: string;
}

export const PRODUCTS: { id: ProductId; name: string }[] = [
    { id: 'sago', name: 'Sago' },
    { id: 'starch', name: 'Starch' },
    { id: 'polishBroken', name: 'Polish Broken' },
    { id: 'vibratorBroken', name: 'Vibrator Broken' },
    { id: 'dryerBroken', name: 'Dryer Broken' },
    { id: 'polishPowder', name: 'Polish Powder' },
];

/**
 * Phase 2: Production Summary for Reports
 * Aggregated production data per unit
 */
export interface ProductionSummary {
  unitId: string;
  unitName: string;
  totalProduction: number; // Total bags produced
  startDate: string;
  endDate: string;
  productBreakdown?: Record<string, number>; // Product -> Quantity
  dailyAverage?: number;
  daysWithData?: number;
}

/**
 * Phase 2: Production Query Parameters
 */
export interface ProductionQueryParams {
  unitId?: string | string[];
  startDate?: string; // ISO date
  endDate?: string; // ISO date
  productName?: string;
  productId?: ProductId;
  status?: ProductionDaily['status'];
  includeAdjustments?: boolean;
}

/**
 * Phase 2: Production Adjustment Entry
 * Records production corrections/adjustments
 */
export interface ProductionAdjustment {
  id?: string;
  entryId: string; // Reference to ProductionDaily
  unitId: string;
  unitName: string;
  date: string; // ISO date
  productName: string;
  originalQuantity: number;
  adjustedQuantity: number;
  adjustmentReason: string;
  adjustmentType: 'correction' | 'audit' | 'manual';
  adjustedBy: string;
  adjustedByName?: string;
  adjustedAt: any;
  
  // Financial impact
  impactOnCost?: number;
  linkedExpenseId?: string; // If adjustment created expense entry
  
  // Approval
  approvedBy?: string;
  approvedAt?: any;
  status: 'pending' | 'approved' | 'rejected';
}
